/* =========================================================
   SnapCoil theme JS
   Vanilla JS, no framework, no build step.
   Handles: mobile nav, cart drawer, AJAX cart (add/update/remove),
   product variant/quantity UI, basic form validation.
   ========================================================= */
(function () {
  "use strict";

  var routes = (window.SnapCoil && window.SnapCoil.routes) || {
    cart_add_url: "/cart/add.js",
    cart_change_url: "/cart/change.js",
    cart_update_url: "/cart/update.js",
    cart_url: "/cart.js",
  };

  /* ---------------- helpers ---------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return "$" + amount;
  }
  function trapFocus(container) {
    var focusable = qsa('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return !el.disabled && el.offsetParent !== null; });
    if (!focusable.length) return;
    focusable[0].focus();
  }
  function renderCartCount(cart) {
    qsa("[data-cart-count]").forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }

  /* ---------------- mobile nav ---------------- */
  function initMobileNav() {
    var nav = qs("[data-mobile-nav]");
    var openBtns = qsa("[data-mobile-nav-open]");
    var closeBtns = qsa("[data-mobile-nav-close]");
    if (!nav) return;

    function open() {
      nav.setAttribute("data-open", "true");
      document.body.classList.add("overflow-hidden-scroll");
      trapFocus(nav);
      document.addEventListener("keydown", onKeydown);
    }
    function close() {
      nav.setAttribute("data-open", "false");
      document.body.classList.remove("overflow-hidden-scroll");
      document.removeEventListener("keydown", onKeydown);
    }
    function onKeydown(e) {
      if (e.key === "Escape") close();
    }
    openBtns.forEach(function (btn) { btn.addEventListener("click", open); });
    closeBtns.forEach(function (btn) { btn.addEventListener("click", close); });
    qs(".mobile-nav__backdrop", nav) && qs(".mobile-nav__backdrop", nav).addEventListener("click", close);
  }

  /* ---------------- cart drawer ---------------- */
  var CartDrawer = (function () {
    var drawer = qs("[data-cart-drawer]");
    if (!drawer) {
      // Cart type is set to "page" in theme settings: no drawer markup is
      // rendered, but the header cart count still needs to stay live.
      return {
        open: function () {},
        close: function () {},
        renderCount: renderCartCount,
        refresh: function () {
          fetch(routes.cart_url).then(function (r) { return r.json(); }).then(renderCartCount).catch(function () {});
        },
      };
    }

    var openBtns = qsa("[data-cart-drawer-open]");
    var closeBtns = qsa("[data-cart-drawer-close]");

    function open() {
      drawer.setAttribute("data-open", "true");
      document.body.classList.add("overflow-hidden-scroll");
      document.addEventListener("keydown", onKeydown);
    }
    function close() {
      drawer.setAttribute("data-open", "false");
      document.body.classList.remove("overflow-hidden-scroll");
      document.removeEventListener("keydown", onKeydown);
    }
    function onKeydown(e) {
      if (e.key === "Escape") close();
    }

    openBtns.forEach(function (btn) { btn.addEventListener("click", function (e) { e.preventDefault(); open(); }); });
    closeBtns.forEach(function (btn) { btn.addEventListener("click", close); });
    qs(".cart-drawer__backdrop", drawer) && qs(".cart-drawer__backdrop", drawer).addEventListener("click", close);

    var renderCount = renderCartCount;

    function renderDrawer(cart) {
      var itemsEl = qs("[data-cart-drawer-items]", drawer);
      var footerEl = qs("[data-cart-drawer-footer]", drawer);
      var emptyEl = qs("[data-cart-drawer-empty]", drawer);
      if (!itemsEl) return;

      if (cart.item_count === 0) {
        itemsEl.innerHTML = "";
        if (emptyEl) emptyEl.hidden = false;
        if (footerEl) footerEl.hidden = true;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      if (footerEl) footerEl.hidden = false;

      itemsEl.innerHTML = cart.items.map(function (item) {
        var img = item.image
          ? '<img class="media-fill" src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, "_160x$1$2") + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy">'
          : '<div class="image-placeholder" style="border-radius:inherit;aspect-ratio:1/1"></div>';
        var variantLine = item.variant_title ? '<p class="cart-item__variant">' + escapeHtml(item.variant_title) + "</p>" : "";
        return (
          '<div class="cart-item" data-cart-item data-line-key="' + item.key + '">' +
            '<div class="cart-item__media">' + img + "</div>" +
            '<div class="cart-item__body">' +
              '<p class="cart-item__title">' + escapeHtml(item.product_title) + "</p>" +
              variantLine +
              '<div class="cart-item__row">' +
                '<div class="qty-stepper" data-qty-stepper>' +
                  '<button type="button" data-qty-decrease aria-label="Decrease quantity">-</button>' +
                  '<input type="number" min="0" value="' + item.quantity + '" data-qty-input aria-label="Quantity" inputmode="numeric">' +
                  '<button type="button" data-qty-increase aria-label="Increase quantity">+</button>' +
                "</div>" +
                '<button type="button" class="cart-item__remove" data-cart-item-remove>Remove</button>' +
              "</div>" +
            "</div>" +
            '<div class="cart-item__price">' + formatMoney(item.final_line_price) + "</div>" +
          "</div>"
        );
      }).join("");

      var subtotalEl = qs("[data-cart-subtotal]", drawer);
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

      bindLineItemEvents(itemsEl);
    }

    function escapeHtml(str) {
      var div = document.createElement("div");
      div.textContent = str || "";
      return div.innerHTML;
    }

    function bindLineItemEvents(scope) {
      qsa("[data-cart-item]", scope).forEach(function (row) {
        var key = row.getAttribute("data-line-key");
        var input = qs("[data-qty-input]", row);
        var dec = qs("[data-qty-decrease]", row);
        var inc = qs("[data-qty-increase]", row);
        var remove = qs("[data-cart-item-remove]", row);

        dec && dec.addEventListener("click", function () {
          var v = Math.max(0, parseInt(input.value || "0", 10) - 1);
          updateLine(key, v);
        });
        inc && inc.addEventListener("click", function () {
          var v = parseInt(input.value || "0", 10) + 1;
          updateLine(key, v);
        });
        input && input.addEventListener("change", function () {
          var v = Math.max(0, parseInt(input.value || "0", 10));
          updateLine(key, v);
        });
        remove && remove.addEventListener("click", function () {
          updateLine(key, 0);
        });
      });
    }

    function updateLine(key, quantity) {
      fetch(routes.cart_change_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderCount(cart);
          renderDrawer(cart);
        })
        .catch(function () {});
    }

    function refresh() {
      fetch(routes.cart_url)
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderCount(cart);
          renderDrawer(cart);
        })
        .catch(function () {});
    }

    // initial paint from embedded JSON if present
    var seedEl = qs("[data-cart-seed]");
    if (seedEl) {
      try {
        var seed = JSON.parse(seedEl.textContent);
        renderCount(seed);
        renderDrawer(seed);
      } catch (e) { /* noop */ }
    }

    return { open: open, close: close, refresh: refresh, renderCount: renderCount, renderDrawer: renderDrawer, updateLine: updateLine };
  })();

  /* ---------------- add-to-cart forms (product page + quick add) ---------------- */
  function initAddToCartForms() {
    qsa("[data-product-form]").forEach(function (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var statusEl = qs("[data-form-status]", form);
        var submitBtn = qs("[data-add-to-cart]", form);
        var idInput = qs('[name="id"]', form);

        if (!idInput || !idInput.value) {
          setStatus(statusEl, "error", "Please select a valid option.");
          return;
        }

        var formData = new FormData(form);
        var payload = {
          items: [
            {
              id: formData.get("id"),
              quantity: parseInt(formData.get("quantity") || "1", 10) || 1,
            },
          ],
        };

        if (submitBtn) { submitBtn.disabled = true; submitBtn.dataset.originalText = submitBtn.textContent; submitBtn.textContent = "Adding..."; }

        fetch(routes.cart_add_url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          })
          .then(function () {
            setStatus(statusEl, "success", "Added to cart.");
            CartDrawer.refresh();
            if ((window.SnapCoil && window.SnapCoil.cartType) !== "page") {
              CartDrawer.open();
            }
          })
          .catch(function (err) {
            var message = (err && err.description) || "Couldn't add that item right now.";
            setStatus(statusEl, "error", message);
          })
          .finally(function () {
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitBtn.dataset.originalText; }
          });
      });
    });
  }

  function setStatus(el, state, message) {
    if (!el) return;
    el.dataset.state = state;
    el.textContent = message;
  }

  /* ---------------- quantity steppers outside cart (product page) ---------------- */
  function initQuantitySteppers() {
    qsa("[data-qty-stepper]").forEach(function (stepper) {
      if (stepper.closest("[data-cart-drawer]") || stepper.closest("[data-cart-page]")) return;
      var input = qs("[data-qty-input]", stepper);
      var dec = qs("[data-qty-decrease]", stepper);
      var inc = qs("[data-qty-increase]", stepper);
      dec && dec.addEventListener("click", function () {
        input.value = Math.max(1, parseInt(input.value || "1", 10) - 1);
      });
      inc && inc.addEventListener("click", function () {
        input.value = parseInt(input.value || "1", 10) + 1;
      });
    });
  }

  /* ---------------- product page variant thumbs ---------------- */
  function initGalleryThumbs() {
    qsa("[data-product-thumb]").forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        var mainImg = qs("[data-product-main-image]");
        var full = thumb.getAttribute("data-full-src");
        if (mainImg && full) {
          if (mainImg.tagName === "IMG") mainImg.src = full;
        }
        qsa("[data-product-thumb]").forEach(function (t) { t.classList.remove("is-active"); });
        thumb.classList.add("is-active");
      });
    });
  }

  /* ---------------- product page variant picker ---------------- */
  function initVariantPicker() {
    qsa("[data-product-form]").forEach(function (form) {
      var jsonEl = form.parentElement && qs("[data-product-variants-json]", form.parentElement);
      if (!jsonEl) return;
      var variants;
      try {
        variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        return;
      }
      var inputs = qsa("[data-variant-input]", form);
      if (!inputs.length) return;

      var optionGroups = qsa("[data-variant-option-index]", form);

      function selectedOptions() {
        return optionGroups.map(function (group) {
          var checked = qs("[data-variant-input]:checked", group);
          return checked ? checked.value : null;
        });
      }

      function findMatch() {
        var selected = selectedOptions();
        return variants.filter(function (v) {
          return selected.every(function (val, i) { return val === null || v.options[i] === val; });
        })[0];
      }

      function applyVariant(variant) {
        var idInput = qs("[data-selected-variant-id]", form);
        var addBtn = qs("[data-add-to-cart]", form);
        var priceWrap = form.parentElement && qs("[data-product-price]", form.parentElement);

        if (!variant) {
          if (idInput) idInput.value = "";
          if (addBtn) { addBtn.disabled = true; addBtn.textContent = addBtn.getAttribute("data-label-unavailable") || "Unavailable"; }
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = addBtn.getAttribute("data-label-add") || "Add to cart";
          } else {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.getAttribute("data-label-sold-out") || "Sold out";
          }
        }

        if (priceWrap) {
          var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          priceWrap.innerHTML =
            '<div class="price price--large' + (onSale ? " price--on-sale" : "") + '">' +
              (onSale
                ? '<span class="price__sale">' + formatMoney(variant.price) + '</span><span class="price__regular">' + formatMoney(variant.compare_at_price) + "</span>"
                : '<span class="price__regular" style="text-decoration:none;color:inherit;font-weight:700;">' + formatMoney(variant.price) + "</span>") +
            "</div>";
        }

        if (variant.featured_image) {
          var mainImg = qs("[data-product-main-image]");
          if (mainImg && mainImg.tagName === "IMG") mainImg.src = variant.featured_image.src;
        }
      }

      inputs.forEach(function (input) {
        input.addEventListener("change", function () {
          applyVariant(findMatch());
        });
      });
    });
  }

  /* ---------------- cart page (non-drawer) quantity handling ---------------- */
  function initCartPage() {
    var page = qs("[data-cart-page]");
    if (!page) return;
    qsa("[data-cart-item]", page).forEach(function (row) {
      var key = row.getAttribute("data-line-key");
      var input = qs("[data-qty-input]", row);
      var dec = qs("[data-qty-decrease]", row);
      var inc = qs("[data-qty-increase]", row);
      var remove = qs("[data-cart-item-remove]", row);

      function push(qty) {
        fetch(routes.cart_change_url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: qty }),
        }).then(function () { window.location.reload(); });
      }

      dec && dec.addEventListener("click", function () { push(Math.max(0, parseInt(input.value || "0", 10) - 1)); });
      inc && inc.addEventListener("click", function () { push(parseInt(input.value || "0", 10) + 1); });
      input && input.addEventListener("change", function () { push(Math.max(0, parseInt(input.value || "0", 10))); });
      remove && remove.addEventListener("click", function () { push(0); });
    });
  }

  /* ---------------- basic form validation (newsletter etc.) ---------------- */
  function initFormValidation() {
    qsa("[data-validate-form]").forEach(function (form) {
      form.addEventListener("submit", function (e) {
        var valid = true;
        qsa("[required]", form).forEach(function (field) {
          field.classList.remove("field-error");
          if (!field.value || (field.type === "email" && !/^\S+@\S+\.\S+$/.test(field.value))) {
            valid = false;
            field.classList.add("field-error");
          }
        });
        if (!valid) e.preventDefault();
      });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initMobileNav();
    initAddToCartForms();
    initQuantitySteppers();
    initGalleryThumbs();
    initVariantPicker();
    initCartPage();
    initFormValidation();
  });

  window.SnapCoilCart = CartDrawer;
})();
